import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { usePermissions } from '../../hooks/usePermissions';
import { ReportsService, type SoldProduct } from '../../services/reports/reports.service';
import { UsersService } from '../../services/users/users.service';
import { SuppliersService } from '../../services/suppliers/suppliers.service';
import { DatePickerInput } from '../ui/DatePickerInput';
import { Listbox, ListboxButton, ListboxOptions, ListboxOption } from '@headlessui/react';
import { getColombiaDate, toColombiaDateString } from '../../utils/date';
import {
  Package,
  Barcode,
  Tag,
  ShoppingBag,
  DollarSign,
  TrendingUp,
  Loader2,
  Search,
  ChevronDown,
  Check,
  ChevronLeft,
  ChevronRight,
  Truck,
  Calendar,
  User,
  AlertTriangle,
  Box,
  Eye,
} from 'lucide-react';
import { ProductSaleHistoryDrawer } from './ProductSaleHistoryDrawer';

const formatCurrency = (amount: number) => {
  return `$${amount.toLocaleString('es-CO')}`;
};

const toTitleCase = (str: string) => {
  if (!str) return str;
  return str
    .toLowerCase()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const LIMIT_OPTIONS = [10, 25, 50, 100];

export const ProductosVendidosContent: React.FC = () => {
  const { isSuperAdmin } = usePermissions();
  const [dateRange, setDateRange] = useState<'today' | 'week' | 'month' | 'custom'>('today');
  const [fechaInicio, setFechaInicio] = useState('');
  const [fechaFin, setFechaFin] = useState('');
  const [vendedorId, setVendedorId] = useState('');
  const [proveedorId, setProveedorId] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [selectedProduct, setSelectedProduct] = useState<SoldProduct | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Debounce para búsqueda (evita peticiones en cada tecla)
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(searchInput);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Calcular fechas según el rango seleccionado (en zona horaria Colombia)
  useEffect(() => {
    const hoy = getColombiaDate();
    const hoyStr = toColombiaDateString(hoy);

    if (dateRange === 'today') {
      setFechaInicio(hoyStr);
      setFechaFin(hoyStr);
    } else if (dateRange === 'week') {
      const inicioSemana = new Date(hoy);
      inicioSemana.setDate(hoy.getDate() - 6);
      setFechaInicio(toColombiaDateString(inicioSemana));
      setFechaFin(hoyStr);
    } else if (dateRange === 'month') {
      const inicioMes = new Date(hoy.getFullYear(), hoy.getMonth(), 1);
      setFechaInicio(toColombiaDateString(inicioMes));
      setFechaFin(hoyStr);
    }
  }, [dateRange]);

  // Reset page cuando cambian filtros o búsqueda
  useEffect(() => {
    setPage(1);
  }, [dateRange, fechaInicio, fechaFin, vendedorId, proveedorId, searchTerm]);

  const { data: vendedores = [] } = useQuery({
    queryKey: ['users-vendedores'],
    queryFn: async () => {
      const res = await UsersService.getUsers();
      return res.data;
    },
    enabled: isSuperAdmin(),
  });

  const { data: proveedores } = useQuery({
    queryKey: ['proveedores-dropdown-reportes'],
    queryFn: async () => {
      const r = await SuppliersService.getAllForSelect();
      return r.success ? r.data : [];
    },
    staleTime: Infinity,
  });

  const { data: response, isLoading, error } = useQuery({
    queryKey: ['sold-products', fechaInicio, fechaFin, vendedorId, proveedorId, searchTerm, page, limit],
    queryFn: async () => {
      const response = await ReportsService.getSoldProducts(
        {
          fechaInicio: fechaInicio || undefined,
          fechaFin: fechaFin || undefined,
          vendedorId: vendedorId || undefined,
          proveedorId: proveedorId || undefined,
          search: searchTerm || undefined,
        },
        page,
        limit
      );
      return response.data;
    },
    enabled: !!fechaInicio && !!fechaFin,
  });

  const products = response?.data || [];
  const meta = response?.meta || { total: 0, page: 1, limit: 20, totalPages: 0, hasNextPage: false, hasPreviousPage: false };

  // Totales agregados vienen en meta (no solo la página actual)
  const totalVentasGlobal = meta.totalVentas ?? 0;
  const totalUnidadesGlobal = meta.totalUnidades ?? 0;
  const totalCostoGlobal = meta.totalCosto ?? 0;
  const totalMargenGlobal = meta.totalMargen ?? 0;

  const totalCantidad = products.reduce((sum, p) => sum + p.cantidadVendida, 0);
  const totalVentas = products.reduce((sum, p) => sum + p.totalVentas, 0);

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="text-red-600 text-xl mb-4">Error al cargar los productos vendidos</div>
        </div>
      </div>
    );
  }

  const dateOptions = [
    { key: 'today' as const, label: 'Hoy' },
    { key: 'week' as const, label: 'Semana' },
    { key: 'month' as const, label: 'Mes' },
    ...(isSuperAdmin() ? [{ key: 'custom' as const, label: 'Personalizado' }] : []),
  ];

  const fromItem = meta.total === 0 ? 0 : (meta.page - 1) * meta.limit + 1;
  const toItem = Math.min(meta.page * meta.limit, meta.total);

  return (
    <div>
      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        <div className="p-4 rounded-xl shadow-sm bg-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Productos Distintos</p>
              <p className="text-2xl font-bold text-gray-900">{meta.total}</p>
            </div>
            <div className="p-3 rounded-lg bg-blue-100 text-blue-600">
              <Package className="w-6 h-6" />
            </div>
          </div>
        </div>

        <div className="p-4 rounded-xl shadow-sm bg-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Unidades Vendidas</p>
              <p className="text-2xl font-bold text-gray-900">{totalUnidadesGlobal.toLocaleString('es-CO')}</p>
            </div>
            <div className="p-3 rounded-lg bg-purple-100 text-purple-600">
              <ShoppingBag className="w-6 h-6" />
            </div>
          </div>
        </div>

        <div className="p-4 rounded-xl shadow-sm bg-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Ventas</p>
              <p className="text-xl font-bold text-gray-900">
                {formatCurrency(totalVentasGlobal)}
              </p>
            </div>
            <div className="p-3 rounded-lg bg-emerald-100 text-emerald-600">
              <DollarSign className="w-6 h-6" />
            </div>
          </div>
        </div>

        <div className="p-4 rounded-xl shadow-sm bg-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Ganancia Total</p>
              <p className="text-xl font-bold text-gray-900">
                {formatCurrency(totalMargenGlobal)}
              </p>
              {totalVentasGlobal > 0 && (
                <p className="text-xs text-gray-400 mt-0.5">
                  {((totalMargenGlobal / totalVentasGlobal) * 100).toFixed(1)}% margen
                </p>
              )}
            </div>
            <div className="p-3 rounded-lg bg-amber-100 text-amber-600">
              <TrendingUp className="w-6 h-6" />
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="rounded-lg shadow-sm border bg-white border-gray-200">
        {/* Filters */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
            <div className="flex flex-wrap items-center gap-2">
              {/* Fechas */}
              <div className="flex rounded-lg border border-gray-200 overflow-hidden">
                {dateOptions.map((opt) => (
                  <button
                    key={opt.key}
                    onClick={() => setDateRange(opt.key)}
                    className={`px-4 py-2 text-sm font-medium transition-colors ${
                      dateRange === opt.key
                        ? 'bg-emerald-600 text-white'
                        : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>

              {dateRange === 'custom' && isSuperAdmin() && (
                <div className="flex items-center gap-2">
                  <DatePickerInput
                    value={fechaInicio}
                    onChange={setFechaInicio}
                    placeholder="Desde"
                  />
                  <span className="text-sm text-gray-500">a</span>
                  <DatePickerInput
                    value={fechaFin}
                    onChange={setFechaFin}
                    placeholder="Hasta"
                  />
                </div>
              )}

              {/* Vendedor */}
              {isSuperAdmin() && (
                <Listbox value={vendedorId} onChange={setVendedorId}>
                  <div className="relative">
                    <ListboxButton className="relative w-[220px] cursor-pointer rounded-lg border border-gray-200 bg-white py-2 pl-3 pr-10 text-left text-sm text-gray-900 hover:border-emerald-300 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all">
                      <span className="block truncate">
                        {vendedorId
                          ? vendedores.find((v: any) => v.id === vendedorId)?.nombreCompleto
                          : 'Todos los vendedores'}
                      </span>
                      <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                        <ChevronDown className="h-4 w-4 text-gray-400" aria-hidden="true" />
                      </span>
                    </ListboxButton>
                    <ListboxOptions
                      transition
                      className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-lg bg-white py-1 text-sm shadow-lg ring-1 ring-black/5 focus:outline-none data-[closed]:scale-95 data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
                    >
                      <ListboxOption
                        value=""
                        className="group relative cursor-pointer select-none py-2 pl-3 pr-9 text-gray-900 data-[focus]:bg-emerald-50 data-[focus]:text-emerald-900 transition-colors"
                      >
                        <span className="block truncate">Todos los vendedores</span>
                        <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-emerald-600 opacity-0 group-data-[selected]:opacity-100">
                          <Check className="h-4 w-4" />
                        </span>
                      </ListboxOption>
                      {vendedores.map((v: any) => (
                        <ListboxOption
                          key={v.id}
                          value={v.id}
                          className="group relative cursor-pointer select-none py-2 pl-3 pr-9 text-gray-900 data-[focus]:bg-emerald-50 data-[focus]:text-emerald-900 transition-colors"
                        >
                          <span className="block truncate">{v.nombreCompleto}</span>
                          <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-emerald-600 opacity-0 group-data-[selected]:opacity-100">
                            <Check className="h-4 w-4" />
                          </span>
                        </ListboxOption>
                      ))}
                    </ListboxOptions>
                  </div>
                </Listbox>
              )}

              {/* Proveedor */}
              <Listbox value={proveedorId} onChange={setProveedorId}>
                <div className="relative">
                  <ListboxButton className="relative w-[220px] cursor-pointer rounded-lg border border-gray-200 bg-white py-2 pl-3 pr-10 text-left text-sm text-gray-900 hover:border-emerald-300 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all">
                    <span className="block truncate">
                      {proveedorId
                        ? proveedores?.find((p: any) => p.id === proveedorId)?.nombre
                        : 'Todos los proveedores'}
                    </span>
                    <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                      <ChevronDown className="h-4 w-4 text-gray-400" aria-hidden="true" />
                    </span>
                  </ListboxButton>
                  <ListboxOptions
                    transition
                    className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-lg bg-white py-1 text-sm shadow-lg ring-1 ring-black/5 focus:outline-none data-[closed]:scale-95 data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
                  >
                    <ListboxOption
                      value=""
                      className="group relative cursor-pointer select-none py-2 pl-3 pr-9 text-gray-900 data-[focus]:bg-emerald-50 data-[focus]:text-emerald-900 transition-colors"
                    >
                      <span className="block truncate">Todos los proveedores</span>
                      <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-emerald-600 opacity-0 group-data-[selected]:opacity-100">
                        <Check className="h-4 w-4" />
                      </span>
                    </ListboxOption>
                    {proveedores?.map((p: any) => (
                      <ListboxOption
                        key={p.id}
                        value={p.id}
                        className="group relative cursor-pointer select-none py-2 pl-3 pr-9 text-gray-900 data-[focus]:bg-emerald-50 data-[focus]:text-emerald-900 transition-colors"
                      >
                        <span className="block truncate">{p.nombre}</span>
                        <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-emerald-600 opacity-0 group-data-[selected]:opacity-100">
                          <Check className="h-4 w-4" />
                        </span>
                      </ListboxOption>
                    ))}
                  </ListboxOptions>
                </div>
              </Listbox>

              {proveedorId && (
                <button
                  onClick={() => setProveedorId('')}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <Truck className="w-4 h-4 text-emerald-600" />
                  <span className="truncate max-w-[150px]">
                    {proveedores?.find((p: any) => p.id === proveedorId)?.nombre}
                  </span>
                  <span className="text-gray-400">×</span>
                </button>
              )}
            </div>

            {/* Búsqueda + Límite */}
            <div className="flex items-center gap-2 w-full lg:w-auto">
              <div className="relative w-full lg:w-72">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Buscar producto, código o categoría..."
                  className="w-full pl-9 pr-9 py-2 rounded-lg border border-gray-200 text-sm bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all"
                />
                {(isLoading || searchInput !== searchTerm) && (
                  <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 animate-spin" />
                )}
              </div>

              <Listbox value={limit} onChange={(val) => { setLimit(val); setPage(1); }}>
                <div className="relative">
                  <ListboxButton className="relative w-[80px] cursor-pointer rounded-lg border border-gray-200 bg-white py-2 pl-3 pr-8 text-left text-sm text-gray-900 hover:border-emerald-300 focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-all">
                    <span className="block truncate">{limit}</span>
                    <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                      <ChevronDown className="h-4 w-4 text-gray-400" />
                    </span>
                  </ListboxButton>
                  <ListboxOptions
                    transition
                    className="absolute right-0 z-50 mt-1 max-h-60 w-full overflow-auto rounded-lg bg-white py-1 text-sm shadow-lg ring-1 ring-black/5 focus:outline-none data-[closed]:scale-95 data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
                  >
                    {LIMIT_OPTIONS.map((opt) => (
                      <ListboxOption
                        key={opt}
                        value={opt}
                        className="group relative cursor-pointer select-none py-2 pl-3 pr-9 text-gray-900 data-[focus]:bg-emerald-50 data-[focus]:text-emerald-900 transition-colors"
                      >
                        <span className="block truncate">{opt}</span>
                        <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-emerald-600 opacity-0 group-data-[selected]:opacity-100">
                          <Check className="h-4 w-4" />
                        </span>
                      </ListboxOption>
                    ))}
                  </ListboxOptions>
                </div>
              </Listbox>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
                  Producto
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
                  Código de Barra
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
                  Categoría
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
                  Proveedor
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
                  Última Venta
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
                  Vendedor
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
                  Stock
                </th>
                <th className="px-6 py-4 text-right text-xs font-semibold uppercase tracking-wider text-gray-600">
                  Cant. Vendida
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
                  Presentaciones
                </th>
                <th className="px-6 py-4 text-right text-xs font-semibold uppercase tracking-wider text-gray-600">
                  Total Ventas
                </th>
                <th className="px-6 py-4 text-right text-xs font-semibold uppercase tracking-wider text-gray-600">
                  Costo
                </th>
                <th className="px-6 py-4 text-right text-xs font-semibold uppercase tracking-wider text-gray-600">
                  Margen
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {isLoading ? (
                <tr>
                  <td colSpan={11} className="px-6 py-12 text-center">
                    <div className="flex items-center justify-center gap-2 text-gray-500">
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Cargando productos...
                    </div>
                  </td>
                </tr>
              ) : products.length === 0 ? (
                <tr>
                  <td colSpan={11} className="px-6 py-12 text-center text-gray-500">
                    <Package className="w-16 h-16 mx-auto mb-4 opacity-50" />
                    <p>
                      {searchTerm
                        ? 'No se encontraron productos que coincidan con la búsqueda'
                        : 'No se encontraron productos vendidos en este período'}
                    </p>
                  </td>
                </tr>
              ) : (
                products.map((product) => (
                  <tr
                    key={product.id}
                    className="hover:bg-gray-50/50 transition-colors cursor-pointer group"
                    onClick={() => {
                      setSelectedProduct(product);
                      setDrawerOpen(true);
                    }}
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-gray-100 group-hover:bg-emerald-100 transition-colors">
                          <Package className="w-4 h-4 text-gray-600 group-hover:text-emerald-600 transition-colors" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900">{toTitleCase(product.nombre)}</p>
                          <p className="text-[10px] text-emerald-600 font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                            Ver historial de ventas
                          </p>
                        </div>
                        <Eye className="w-4 h-4 text-gray-300 group-hover:text-emerald-500 opacity-0 group-hover:opacity-100 transition-all" />
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <Barcode className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-700">
                          {product.codigoBarras || '—'}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                        <Tag className="w-3 h-3" />
                        {toTitleCase(product.categoria)}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="inline-flex items-center gap-1.5 text-sm text-gray-700">
                        <Truck className="w-3.5 h-3.5 text-gray-400" />
                        {product.proveedor || '—'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1.5">
                        <Calendar className="w-3.5 h-3.5 text-gray-400" />
                        <span className="text-sm text-gray-700">
                          {product.ultimaVenta
                            ? new Date(product.ultimaVenta).toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' })
                            : '—'}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1.5">
                        <User className="w-3.5 h-3.5 text-gray-400" />
                        <span className="text-sm text-gray-700">
                          {product.vendedores || '—'}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="space-y-1.5">
                        {/* Stock Base indicator */}
                        <div className="flex items-center gap-2">
                          <Box className="w-3.5 h-3.5 text-gray-400" />
                          <span className={`text-sm font-semibold ${
                            product.stockBase <= 0
                              ? 'text-red-600'
                              : product.stockBase <= 5
                              ? 'text-yellow-600'
                              : 'text-emerald-600'
                          }`}>
                            {product.stockBase.toLocaleString('es-CO')}
                          </span>
                          <span className="text-xs text-gray-400">base</span>
                          {product.stockBase <= 0 && (
                            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-red-100 text-red-700">
                              <AlertTriangle className="w-3 h-3" />
                              Agotado
                            </span>
                          )}
                          {product.stockBase > 0 && product.stockBase <= 5 && (
                            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-yellow-100 text-yellow-700">
                              <AlertTriangle className="w-3 h-3" />
                              Bajo
                            </span>
                          )}
                        </div>
                        {/* Presentaciones compactas */}
                        {product.presentaciones.length > 0 && (
                          <div className="flex flex-wrap gap-1">
                            {product.presentaciones.map((pres) => (
                              <span
                                key={pres.nombre}
                                className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium ${
                                  pres.stock <= 0
                                    ? 'bg-red-50 text-red-600'
                                    : pres.stock <= 5
                                    ? 'bg-yellow-50 text-yellow-600'
                                    : 'bg-emerald-50 text-emerald-600'
                                }`}
                                title={`Factor: ${pres.factor}`}
                              >
                                {pres.nombre}: {pres.stock.toLocaleString('es-CO')}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <TrendingUp className="w-4 h-4 text-emerald-500" />
                        <span className="font-semibold text-gray-900">
                          {product.cantidadVendida.toLocaleString('es-CO')}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1">
                        {product.ventasPorPresentacion.length > 0 ? (
                          product.ventasPorPresentacion.map((vp) => (
                            <span
                              key={vp.nombre}
                              className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-violet-50 text-violet-600"
                            >
                              {vp.nombre}: {vp.cantidad}
                            </span>
                          ))
                        ) : (
                          <span className="text-xs text-gray-400">—</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <span className="font-semibold text-gray-900">
                        {formatCurrency(product.totalVentas)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <span className="text-sm text-gray-600">
                        {formatCurrency(product.costoPromedio)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <span className={`text-sm font-semibold ${product.margen >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        {formatCurrency(product.margen)}
                      </span>
                      {product.totalVentas > 0 && (
                        <p className="text-xs text-gray-400">
                          {((product.margen / product.totalVentas) * 100).toFixed(1)}%
                        </p>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {!isLoading && meta.totalPages > 0 && (
          <div className="px-6 py-4 border-t border-gray-200">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              {/* Info */}
              <div className="text-sm text-gray-500">
                Mostrando <span className="font-medium text-gray-900">{fromItem}</span>–
                <span className="font-medium text-gray-900">{toItem}</span> de{' '}
                <span className="font-medium text-gray-900">{meta.total}</span> productos
              </div>

              <div className="flex items-center gap-3">
                {/* Page nav */}
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={!meta.hasPreviousPage}
                    className="p-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>

                  {Array.from({ length: meta.totalPages }, (_, i) => i + 1).map((p) => (
                    <button
                      key={p}
                      onClick={() => setPage(p)}
                      className={`min-w-[36px] h-9 px-2 rounded-lg text-sm font-medium transition-colors ${
                        p === meta.page
                          ? 'bg-emerald-600 text-white'
                          : 'text-gray-600 hover:bg-gray-50 border border-gray-200'
                      }`}
                    >
                      {p}
                    </button>
                  ))}

                  <button
                    onClick={() => setPage((p) => Math.min(meta.totalPages, p + 1))}
                    disabled={!meta.hasNextPage}
                    className="p-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Drawer: Historial de ventas del producto */}
      <ProductSaleHistoryDrawer
        isOpen={drawerOpen}
        product={selectedProduct}
        filters={{
          fechaInicio: fechaInicio || undefined,
          fechaFin: fechaFin || undefined,
          vendedorId: vendedorId || undefined,
          proveedorId: proveedorId || undefined,
        }}
        onClose={() => {
          setDrawerOpen(false);
          setSelectedProduct(null);
        }}
      />
    </div>
  );
};
